/**
 * SatelliteWizard — self-service "create a tool" form.
 * A colleague describes a tool, picks one or more backend capabilities from the catalog
 * (and/or requests a brand-new one via "Iets anders"), and submits a request an admin
 * approves. Catalog-only requests are directly buildable; anything else → review gate.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const STATUS_LABEL = {
  new: 'In wachtrij', needs_capability_review: 'Wacht op capability-review',
  approved: 'Goedgekeurd', provisioning: 'Provisioning', building: 'Wordt gebouwd',
  deployed: 'Uitgerold', live: 'Live', failed: 'Mislukt', rejected: 'Afgewezen',
  capability_drafting: 'Capability wordt opgesteld', capability_drafted: 'Capability wacht op review',
  capability_rejected: 'Capability afgewezen',
};

export default function SatelliteWizard() {
  const [displayName, setDisplayName] = useState('');
  const [goal, setGoal] = useState('');
  const [criteria, setCriteria] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState({ 'llm-text-rewrite': true });   // key → bool
  const [otherChecked, setOtherChecked] = useState(false);
  const [otherText, setOtherText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [mine, setMine] = useState([]);

  const slug = slugify(displayName);
  const selectedKeys = catalog.filter((c) => selected[c.key]).map((c) => c.key);

  const loadMine = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/satellite-requests/mine`);
      if (r.ok) setMine(await r.json());
    } catch (_) {}
  }, []);

  const loadCatalog = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/satellite-capabilities`);
      if (r.ok) setCatalog((await r.json()).capabilities || []);
    } catch (_) {}
  }, []);

  useEffect(() => { loadMine(); loadCatalog(); }, [loadMine, loadCatalog]);

  const toggle = (key) => setSelected((s) => ({ ...s, [key]: !s[key] }));

  const submit = async () => {
    setMsg(null);
    if (!displayName.trim() || !goal.trim()) { setMsg({ bad: true, t: 'Vul naam en doel in.' }); return; }
    if (!selectedKeys.length && !(otherChecked && otherText.trim())) {
      setMsg({ bad: true, t: 'Kies minstens één capability of beschrijf wat je nodig hebt onder "Iets anders".' });
      return;
    }
    setSubmitting(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/satellite-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tool_slug: slug,
          display_name: displayName.trim(),
          goal_description: goal.trim(),
          acceptance_criteria: criteria.split('\n').map((s) => s.trim()).filter(Boolean),
          requested_capabilities: selectedKeys,
          new_capability_request: otherChecked ? otherText.trim() : null,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).error || r.status);
      const created = await r.json();
      setMsg({ bad: false, t: created.status === 'needs_capability_review'
        ? 'Aangevraagd — deze tool heeft een nieuwe backend-capability nodig en wacht op review.'
        : 'Aangevraagd! Een admin keurt de tool goed, daarna wordt hij automatisch gebouwd.' });
      setDisplayName(''); setGoal(''); setCriteria('');
      setSelected({ 'llm-text-rewrite': true }); setOtherChecked(false); setOtherText('');
      loadMine();
    } catch (e) {
      setMsg({ bad: true, t: 'Mislukt: ' + e.message });
    } finally { setSubmitting(false); }
  };

  const L = { display: 'block', fontSize: 12, color: '#64748b', margin: '14px 0 4px', fontWeight: 600 };
  const I = { width: '100%', padding: '9px 11px', border: '1px solid #cbd5e1', borderRadius: 8, font: 'inherit', fontSize: 14, boxSizing: 'border-box' };
  const capBox = (on) => ({ border: `1px solid ${on ? '#7c5cff' : '#e2e8f0'}`, background: on ? '#f5f3ff' : '#fff', borderRadius: 8, padding: '10px 12px', marginBottom: 8, cursor: 'pointer' });

  return (
    <div style={{ maxWidth: 640 }}>
      <h3 style={{ margin: '0 0 4px' }}>Nieuwe satelliet-tool</h3>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0 }}>
        Beschrijf je tool. Na goedkeuring bouwt de AI-agent de tool en zet hem live achter je Full Frame-login.
      </p>

      <label style={L}>Naam</label>
      <input style={I} value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="bijv. Klant-update mailer" />
      {slug && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>url: /{slug}</div>}

      <label style={L}>Wat moet de tool doen?</label>
      <textarea style={{ ...I, minHeight: 90, resize: 'vertical' }} value={goal} onChange={(e) => setGoal(e.target.value)}
        placeholder="bijv. Een tekstveld waar ik projectstatus in plak, en er komt een nette klant-update mail uit in Full Frame-tone." />

      <label style={L}>Acceptatiecriteria (één per regel, optioneel)</label>
      <textarea style={{ ...I, minHeight: 70, resize: 'vertical' }} value={criteria} onChange={(e) => setCriteria(e.target.value)}
        placeholder={'Login met FF-account werkt\nInvoer > uitvoer in < 5s\nUitvoer is in het Nederlands'} />

      <label style={L}>Welke backend-capabilities heeft de tool nodig? (meerdere mogelijk)</label>
      {catalog.map((c) => (
        <div key={c.key} style={capBox(!!selected[c.key])} onClick={() => toggle(c.key)}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', margin: 0 }}>
            <input type="checkbox" checked={!!selected[c.key]} onChange={() => toggle(c.key)} style={{ marginTop: 2 }} />
            <span>
              <b style={{ fontSize: 13.5 }}>{c.label}</b>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{c.hint}</div>
            </span>
          </label>
        </div>
      ))}

      {/* Iets anders — request a brand-new backend capability (human review gate). */}
      <div style={capBox(otherChecked)} onClick={() => setOtherChecked((v) => !v)}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', margin: 0 }}>
          <input type="checkbox" checked={otherChecked} onChange={() => setOtherChecked((v) => !v)} style={{ marginTop: 2 }} />
          <span>
            <b style={{ fontSize: 13.5 }}>Iets anders (nieuwe backend-capability nodig)</b>
            <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>
              Beschrijf wat je nodig hebt; een mens schrijft + reviewt de nieuwe backend-capability.
            </div>
          </span>
        </label>
        {otherChecked && (
          <textarea
            style={{ ...I, minHeight: 70, resize: 'vertical', marginTop: 8 }}
            value={otherText}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setOtherText(e.target.value)}
            placeholder={'Beschrijf precies welke data of acties je nodig hebt uit de backend.\nbijv. "Lees openstaande offertes per klant" of "Schrijf een regel in tabel X".'}
          />
        )}
      </div>

      <div style={{ marginTop: 18 }}>
        <button onClick={submit} disabled={submitting}
          style={{ background: '#7c5cff', color: '#fff', border: 0, borderRadius: 8, padding: '10px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer', opacity: submitting ? 0.6 : 1 }}>
          {submitting ? 'Versturen…' : 'Tool aanvragen'}
        </button>
      </div>
      {msg && <div style={{ marginTop: 12, fontSize: 13, color: msg.bad ? '#dc2626' : '#0f766e' }}>{msg.t}</div>}

      {mine.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <h4 style={{ margin: '0 0 8px' }}>Mijn aanvragen</h4>
          {mine.map((m) => (
            <div key={m.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <b style={{ fontSize: 14 }}>{m.display_name} <span style={{ color: '#94a3b8', fontWeight: 400 }}>/{m.tool_slug}</span></b>
                <span style={{ fontSize: 12, color: '#475569' }}>{STATUS_LABEL[m.status] || m.status}</span>
              </div>
              {Array.isArray(m.requested_capabilities) && m.requested_capabilities.length > 0 && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>caps: {m.requested_capabilities.join(', ')}</div>
              )}
              {m.swa_url && <a href={m.swa_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#7c5cff' }}>{m.swa_url}</a>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
